// React-Query-Hooks fuer die Cloud-API (G1/G2).
//
// Konvention: list/detail-Hook = GET, mutate-Hook = POST/PATCH + Invalidierung
// der betroffenen Query-Keys, damit sich die UI nach jeder Aktion aktualisiert.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  ApiError,
  apiDelete,
  apiGet,
  apiPatchJson,
  apiPostForm,
  apiPostJson,
  clearTokens,
  hasTokens,
  setTokens,
} from "@/api/client";
import type {
  AdminOverview,
  Baustein,
  DatasetSummary,
  ReviewItem,
  AssignMandantBody,
  BelegDetail,
  BelegListItem,
  BelegListResponse,
  BelegPdfResponse,
  BelegStatus,
  BulkInviteResult,
  CurrentUser,
  HealthOut,
  InvitationListItem,
  InvitationResponse,
  LoginBody,
  Mandant,
  MandantCreateBody,
  MandantListResponse,
  MandantUpdateBody,
  TokenResponse,
  UploadResult,
} from "@/api/types";

const POLL_INTERVAL_LIST_MS = 15_000;

// --- Query-Keys (eine Stelle, nicht ueber das ganze UI verstreut) ---
export const qk = {
  health: ["health"] as const,
  belege: (status?: BelegStatus) =>
    status ? (["belege", status] as const) : (["belege"] as const),
  beleg: (id: string) => ["beleg", id] as const,
  belegPdf: (id: string) => ["beleg-pdf", id] as const,
  mandanten: (aktiv?: boolean) =>
    aktiv === undefined
      ? (["mandanten"] as const)
      : (["mandanten", aktiv] as const),
};

// --- Auth ---------------------------------------------------------

export function useCurrentUser(): UseQueryResult<CurrentUser | null, Error> {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      // Ohne Token gar nicht erst fragen — spart einen 401 beim ersten Laden.
      if (!hasTokens()) return null;
      try {
        return await apiGet<CurrentUser>("/api/auth/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useLogin(): UseMutationResult<CurrentUser, Error, LoginBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const tokens = await apiPostJson<TokenResponse>("/api/auth/login", body);
      setTokens({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
      });
      return apiGet<CurrentUser>("/api/auth/me");
    },
    onSuccess: (user) => {
      qc.setQueryData(["auth", "me"], user);
    },
  });
}

export function useLogout(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    // Stateless JWT: Logout ist rein client-seitig (kein Server-Endpunkt).
    mutationFn: async () => {
      clearTokens();
    },
    onSuccess: () => {
      qc.setQueryData(["auth", "me"], null);
      qc.invalidateQueries();
    },
  });
}

// --- Reads ---------------------------------------------------------

export function useHealth(): UseQueryResult<HealthOut, Error> {
  return useQuery({
    queryKey: qk.health,
    queryFn: () => apiGet<HealthOut>("/api/health"),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useBelege(
  status?: BelegStatus
): UseQueryResult<BelegListItem[], Error> {
  const params = new URLSearchParams({ limit: "100" });
  if (status) params.set("status", status);
  return useQuery({
    queryKey: qk.belege(status),
    queryFn: async () =>
      (await apiGet<BelegListResponse>(`/api/belege?${params}`)).items,
    refetchInterval: POLL_INTERVAL_LIST_MS,
  });
}

export function useBeleg(id: string): UseQueryResult<BelegDetail, Error> {
  return useQuery({
    queryKey: qk.beleg(id),
    queryFn: () => apiGet<BelegDetail>(`/api/belege/${id}`),
  });
}

// PDF: die Cloud liefert eine Presigned-S3-URL als JSON (ADR-016). Der
// PdfViewer nutzt die `url` direkt. Cache knapp unter der TTL halten.
export function useBelegPdfUrl(id: string): UseQueryResult<string, Error> {
  return useQuery({
    queryKey: qk.belegPdf(id),
    queryFn: async () =>
      (await apiGet<BelegPdfResponse>(`/api/belege/${id}/pdf`)).url,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useMandanten(
  aktiv: boolean | undefined = true
): UseQueryResult<Mandant[], Error> {
  const params = new URLSearchParams({ limit: "100" });
  if (aktiv !== undefined) params.set("aktiv", String(aktiv));
  return useQuery({
    queryKey: qk.mandanten(aktiv),
    queryFn: async () =>
      (await apiGet<MandantListResponse>(`/api/mandanten?${params}`)).items,
  });
}

export function useCreateMandant(): UseMutationResult<
  Mandant,
  Error,
  MandantCreateBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiPostJson<Mandant>("/api/mandanten", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mandanten"] });
    },
  });
}

export function useUpdateMandant(
  mandantId: string
): UseMutationResult<Mandant, Error, MandantUpdateBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPatchJson<Mandant>(`/api/mandanten/${mandantId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mandanten"] });
    },
  });
}

// --- Beleg-Mutations ----------------------------------------------

function useBelegMutationInvalidator(belegId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: qk.beleg(belegId) });
    qc.invalidateQueries({ queryKey: ["belege"] });
  };
}

export function useAssignMandant(
  belegId: string
): UseMutationResult<BelegDetail, Error, AssignMandantBody> {
  const invalidate = useBelegMutationInvalidator(belegId);
  return useMutation({
    mutationFn: (body) =>
      apiPostJson<BelegDetail>(`/api/belege/${belegId}/mandant`, body),
    onSuccess: () => invalidate(),
  });
}

export function useBestaetigen(
  belegId: string
): UseMutationResult<BelegDetail, Error, void> {
  const invalidate = useBelegMutationInvalidator(belegId);
  return useMutation({
    mutationFn: () =>
      apiPostJson<BelegDetail>(`/api/belege/${belegId}/bestaetigen`, {}),
    onSuccess: () => invalidate(),
  });
}

export function useVerwerfen(
  belegId: string
): UseMutationResult<BelegDetail, Error, void> {
  const invalidate = useBelegMutationInvalidator(belegId);
  return useMutation({
    mutationFn: () =>
      apiPostJson<BelegDetail>(`/api/belege/${belegId}/verwerfen`, {}),
    onSuccess: () => invalidate(),
  });
}

export interface UploadArgs {
  file: File;
  mandantKundennr?: string;
}

export function useUpload(): UseMutationResult<UploadResult, Error, UploadArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, mandantKundennr }) => {
      const fd = new FormData();
      fd.append("file", file);
      if (mandantKundennr) fd.append("mandant_bmd_kundennr", mandantKundennr);
      return apiPostForm<UploadResult>("/api/upload", fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["belege"] });
    },
  });
}

// --- Klient-Einladungen (G3) ---

export function useInvitations(
  mandantId: string
): UseQueryResult<InvitationListItem[], Error> {
  return useQuery({
    queryKey: ["invitations", mandantId],
    queryFn: () =>
      apiGet<InvitationListItem[]>(
        `/api/invitations?mandant_id=${mandantId}`
      ),
  });
}

// Optionale Ziel-Adresse: leer/weggelassen => die Cloud mailt nicht, der Link
// dient als manueller Fallback (mail_sent=false in der Antwort).
export interface CreateInvitationVars {
  contactEmail?: string;
}

export function useCreateInvitation(
  mandantId: string
): UseMutationResult<InvitationResponse, Error, CreateInvitationVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactEmail }) =>
      apiPostJson<InvitationResponse>("/api/invitations", {
        mandant_id: mandantId,
        // leerer String => undefined, damit das Feld im JSON wegfaellt
        contact_email: contactEmail?.trim() || undefined,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["invitations", mandantId] }),
  });
}

// Bulk-Einladung: CSV (Kundennr + E-Mail) hochladen -> viele Einladungen + Mails.
export function useBulkInvite(): UseMutationResult<BulkInviteResult, Error, File> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return apiPostForm<BulkInviteResult>("/api/invitations/bulk", fd);
    },
    // Alle Mandanten-Einladungslisten koennen sich geaendert haben.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invitations"] }),
  });
}

export function useRevokeInvitation(
  mandantId: string
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiDelete(`/api/invitations/${invitationId}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["invitations", mandantId] }),
  });
}

// --- Admin-Cockpit (read-only, ADR-030 Phase 0) ---

export function useAdminOverview(): UseQueryResult<AdminOverview, Error> {
  return useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => apiGet<AdminOverview>("/api/admin/overview"),
  });
}

export function useDatasets(): UseQueryResult<DatasetSummary[], Error> {
  return useQuery({
    queryKey: ["admin", "datasets"],
    queryFn: () => apiGet<DatasetSummary[]>("/api/admin/datasets"),
  });
}

export function useBausteine(): UseQueryResult<Baustein[], Error> {
  return useQuery({
    queryKey: ["admin", "bausteine"],
    queryFn: () => apiGet<Baustein[]>("/api/admin/bausteine"),
  });
}

export function useReviewQueue(): UseQueryResult<ReviewItem[], Error> {
  return useQuery({
    queryKey: ["admin", "review"],
    queryFn: () => apiGet<ReviewItem[]>("/api/admin/review"),
  });
}
